import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import TableComponent from './TableComponent';
import ChartComponent from './ChartComponent';
import { Chart } from 'chart.js';
import Print from '@mui/icons-material/Print';
import AddOnProtectedWrapper from '../../../shared/protectedWrappers/AddOnProtectedWrapper';
import { useSelector } from 'react-redux';
import { CUSTOM_ANALYTICS_SECTION_DESCRIPTION_STYLE, CUSTOM_ANALYTICS_SECTION_TITLE_STYLE } from '../chartTypography';
import { normalizeAnalyticsEntityTitleCase } from '../analyticsTextUtils';
import html2canvas from 'html2canvas';

const categoryDisplayOrder = [
  {
    name: 'Team workload & ownership',
    description: "Who's carrying what, and where capacity is going",
  },
  {
    name: 'Workload & workflow health',
    description: "What's in flight, what's done, and where work is piling up",
  },
  {
    name: 'Work mix',
    description: 'What kinds of work are we doing?',
  },
  {
    name: 'Topics: Overall',
    description: 'The most common themes across all Media Interactions',
  },
  {
    name: 'Topics: Issue response',
    description: 'What issues are we responding to most?',
  },
  {
    name: 'Topics: Proactive focus',
    description: 'What themes are we leading with proactively?',
  },
  {
    name: 'Campaigns, units & organizational reporting',
    description: "How activity maps to campaigns and where it's landing across the organization",
  },
  {
    name: 'Media landscape & trends',
    description: 'Which outlets and spokespeople are most involved, and how things are trending',
  },
  {
    name: 'Engagement Log insights',
    description: 'Stakeholder Engagement patterns outside of Media Interactions',
  },
  {
    name: 'Logs (full view)',
    description: 'The complete interaction and Service logs for your selected filters',
  },
];

const useStyles = makeStyles(() => ({
  outerContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    margin: 0,
    padding: 0,
  },
  gridContainer: {
    width: '90%',
    display: 'flex',
    flexDirection: 'column',
    margin: 0,
    padding: 0,
  },
  sectionGroup: {
    margin: 0,
    width: '100%',
    marginTop: '16px',
    '&:first-of-type': {
      marginTop: 0,
    },
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'column',
    width: '100%',
    paddingTop: '10px',
    marginBottom: '16px',
  },
  sectionHeaderTopRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    gap: '10px',
  },
  sectionTitle: {
    ...CUSTOM_ANALYTICS_SECTION_TITLE_STYLE,
    whiteSpace: 'nowrap',
  },
  sectionDescription: {
    ...CUSTOM_ANALYTICS_SECTION_DESCRIPTION_STYLE,
    marginTop: '6px',
  },
  sectionDivider: {
    flex: 1,
    border: 0,
    borderTop: '1px solid rgba(0, 0, 0, 0.10)',
    margin: 0,
  },
  sectionCardsGrid: {
    width: '100%',
    margin: 0,
    padding: 0,
    boxSizing: 'border-box',
  },
  printSection: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: '16px',
    marginBottom: '32px',
    padding: 0,
  },
  printButton: {
    margin: 0,
  },
  exportActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
}));

const toTitleCase = (value) =>
  value.replaceAll(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
const normalizeSectionDescription = (value) =>
  typeof value === 'string' ? normalizeAnalyticsEntityTitleCase(value.replace(/\.\s*$/, '').trimEnd()) : value;

function AnalyticsTable(props) {
  //Component for arranging in grid and rendering all the table/chart components
  const classes = useStyles();
  const {
    chartDataArray,
    tableDataArray,
    query,
    dateMI,
    changeYearMI,
    dateQuery,
    dateService,
    changeYearService,
    dateServiceQuery,
    dateEngagement,
    changeYearEngagement,
    dateEngagementQuery,
    sectionDisplayOrder = categoryDisplayOrder,
  } = props;
  const printRef = useRef();
  const addonControls = useSelector((state) => state?.user?.addonControls || {});
  const [isCapturingSnapshot, setIsCapturingSnapshot] = useState(false);
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => resolve()));

  const getChartInstances = () => {
    const instances = Chart.instances;
    if (!instances) {
      return [];
    }
    if (typeof instances.values === 'function') {
      return Array.from(instances.values());
    }
    return Object.values(instances);
  };

  const getCanvasSignature = (rootNode) => {
    const canvases = Array.from(rootNode.querySelectorAll('[data-export-card="chart"] canvas'));
    if (canvases.length === 0) {
      return '';
    }

    return canvases
      .map((canvasNode) => {
        try {
          const tail = canvasNode.toDataURL('image/png').slice(-128);
          return `${canvasNode.width}x${canvasNode.height}:${tail}`;
        } catch (error) {
          if (process.env.NODE_ENV !== 'production') {
            console.warn('Canvas signature read failed', error);
          }
          return `${canvasNode.width}x${canvasNode.height}:unreadable`;
        }
      })
      .join('|');
  };

  const waitForFontsReady = async () => {
    if (!document.fonts) {
      return;
    }
    try {
      await document.fonts.ready;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('font readiness failed', error);
      }
      // Continue even if font readiness fails.
    }
  };

  const hasZeroSizedCanvas = (rootNode) =>
    Array.from(rootNode.querySelectorAll('canvas')).some((canvasNode) => {
      const rect = canvasNode.getBoundingClientRect();
      return canvasNode.width === 0 || canvasNode.height === 0 || rect.width === 0 || rect.height === 0;
    });

  const isCanvasSignatureStable = ({ requireStableCanvasSignature, canvasSignature, previousCanvasSignature }) =>
    !requireStableCanvasSignature || (canvasSignature.length > 0 && canvasSignature === previousCanvasSignature);

  const getSnapshotReadiness = ({ rootNode, requireStableCanvasSignature, previousCanvasSignature }) => {
    const hasLoadingSpinner = Boolean(rootNode.querySelector('.spinner-border'));
    const hasAnimatingWordCloud = Boolean(rootNode.querySelector('.animated-word'));
    const hasAnimatingChart = getChartInstances().some((chartInstance) => chartInstance?.animating);
    const hasZeroSizedCanvasNode = hasZeroSizedCanvas(rootNode);
    const canvasSignature = requireStableCanvasSignature ? getCanvasSignature(rootNode) : '';
    const hasStableCanvasSignature = isCanvasSignatureStable({
      requireStableCanvasSignature,
      canvasSignature,
      previousCanvasSignature,
    });

    const isReady =
      !hasLoadingSpinner &&
      !hasAnimatingWordCloud &&
      !hasAnimatingChart &&
      !hasZeroSizedCanvasNode &&
      hasStableCanvasSignature;

    return { isReady, canvasSignature };
  };

  const stopChartAnimations = () => {
    for (const chartInstance of getChartInstances()) {
      try {
        chartInstance.stop?.();
        chartInstance.update?.('none');
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Failed to stop chart animation', error);
        }
        // Ignore single-chart failures and continue.
      }
    }
  };

  const waitForSnapshotReady = async ({ requireStableCanvasSignature = false } = {}) => {
    const rootNode = printRef.current;
    if (!rootNode) {
      return;
    }

    await waitForFontsReady();

    const maxWaitMs = 7000;
    const pollIntervalMs = 120;
    const start = Date.now();

    let stableChecks = 0;
    let previousCanvasSignature = '';
    while (Date.now() - start < maxWaitMs) {
      const { isReady, canvasSignature } = getSnapshotReadiness({
        rootNode,
        requireStableCanvasSignature,
        previousCanvasSignature,
      });
      if (isReady) {
        stableChecks += 1;
        if (stableChecks >= 3) {
          break;
        }
      } else {
        stableChecks = 0;
      }
      previousCanvasSignature = canvasSignature;

      await sleep(pollIntervalMs);
    }

    stopChartAnimations();

    await nextFrame();
    await nextFrame();
    await sleep(250);
  };

  const printComponent = async () => {
    if (isCapturingSnapshot) {
      return;
    }
    let previousAnimationOptions = [];
    try {
      setIsCapturingSnapshot(true);
      document.body.classList.add('capturingSnapshot');
      await waitForSnapshotReady();
      const chartInstances = getChartInstances();
      previousAnimationOptions = chartInstances.map((chartInstance) => ({
        chartInstance,
        animation: chartInstance?.options?.animation,
        animations: chartInstance?.options?.animations,
      }));

      for (const { chartInstance } of previousAnimationOptions) {
        if (!chartInstance) {
          continue;
        }
        if (!chartInstance.options) {
          chartInstance.options = {};
        }
        chartInstance.options.animation = false;
        chartInstance.options.animations = false;
        chartInstance.update?.('none');
      }

      await nextFrame();
      await waitForSnapshotReady({ requireStableCanvasSignature: true });
      const rootNode = printRef.current;
      if (rootNode) {
        const canvas = await html2canvas(rootNode, {
          backgroundColor: '#ffffff',
          useCORS: true,
          scale: 2,
          width: rootNode.scrollWidth,
          height: rootNode.scrollHeight,
          scrollX: 0,
          scrollY: -window.scrollY,
          onclone: (clonedDoc) => {
            clonedDoc.body.classList.add('capturingSnapshot');
          },
        });

        const imageData = canvas.toDataURL('image/jpeg', 1);
        const link = document.createElement('a');
        link.href = imageData;
        link.download = `analytics-snapshot-${Date.now()}.jpg`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } finally {
      for (const { chartInstance, animation, animations } of previousAnimationOptions) {
        if (!chartInstance) {
          continue;
        }
        chartInstance.options.animation = animation;
        chartInstance.options.animations = animations;
        chartInstance.update?.('none');
      }
      document.body.classList.remove('capturingSnapshot');
      setIsCapturingSnapshot(false);
    }
  };

  const getItemsByCategory = (categoryName) => {
    const charts = chartDataArray.filter((item) => {
      if (item.category !== categoryName) {
        return false;
      }
      if (!item.addOn) {
        return true;
      }
      return Boolean(addonControls[item.addOn]);
    });
    const tables = tableDataArray.filter((item) => item.category === categoryName);
    return { charts, tables };
  };

  const sectionsToRender = sectionDisplayOrder
    .map(({ name, description }) => {
      const { charts, tables } = getItemsByCategory(name);
      return { categoryName: name, description, charts, tables };
    })
    .filter(({ charts, tables }) => charts.length > 0 || tables.length > 0);
  const hasCategorizedItems =
    chartDataArray.some((item) => typeof item?.category === 'string' && item.category.trim()) ||
    tableDataArray.some((item) => typeof item?.category === 'string' && item.category.trim());
  const shouldUseFlatLayout = !hasCategorizedItems || sectionsToRender.length === 0;

  return (
    <div className={classes.outerContainer} ref={printRef}>
      <div className={classes.gridContainer}>
        {sectionsToRender.map(({ categoryName, description, charts, tables }) => (
          <div key={categoryName} className={classes.sectionGroup}>
            <div className={classes.sectionHeader}>
              <div className={classes.sectionHeaderTopRow}>
                <Typography className={classes.sectionTitle}>{toTitleCase(categoryName)}</Typography>
                <hr className={classes.sectionDivider} />
              </div>
              {description && (
                <Typography className={classes.sectionDescription}>
                  {normalizeSectionDescription(description)}
                </Typography>
              )}
            </div>
            <Grid
              container
              rowSpacing={3}
              columnSpacing={3}
              justifyContent="flex-start"
              className={classes.sectionCardsGrid}
            >
              {charts.map((chartData, i) => (
                <AddOnProtectedWrapper
                  key={`${categoryName}-chart-${chartData.queryKey ?? i}`}
                  addOnType={chartData.addOn}
                >
                  <ChartComponent
                    chartDataIn={chartData}
                    query={query}
                    dateMI={dateMI}
                    changeYearMI={changeYearMI}
                    dateService={dateService}
                    changeYearService={changeYearService}
                    dateQuery={dateQuery}
                    dateServiceQuery={dateServiceQuery}
                    dateEngagement={dateEngagement}
                    changeYearEngagement={changeYearEngagement}
                    dateEngagementQuery={dateEngagementQuery}
                  ></ChartComponent>
                </AddOnProtectedWrapper>
              ))}
              {tables.map((tableData, i) => (
                <TableComponent
                  key={`${categoryName}-table-${tableData.queryKey ?? i}`}
                  tableData={tableData}
                  query={query}
                >
                  {' '}
                </TableComponent>
              ))}
            </Grid>
          </div>
        ))}
        {shouldUseFlatLayout && (
          <Grid
            container
            rowSpacing={3}
            columnSpacing={3}
            justifyContent="flex-start"
            className={classes.sectionCardsGrid}
          >
            {chartDataArray.map((chartData, i) => (
              <AddOnProtectedWrapper key={`flat-chart-${chartData.queryKey ?? i}`} addOnType={chartData.addOn}>
                <ChartComponent
                  chartDataIn={chartData}
                  query={query}
                  dateMI={dateMI}
                  changeYearMI={changeYearMI}
                  dateService={dateService}
                  changeYearService={changeYearService}
                  dateQuery={dateQuery}
                  dateServiceQuery={dateServiceQuery}
                  dateEngagement={dateEngagement}
                  changeYearEngagement={changeYearEngagement}
                  dateEngagementQuery={dateEngagementQuery}
                ></ChartComponent>
              </AddOnProtectedWrapper>
            ))}
            {tableDataArray.map((tableData, i) => (
              <TableComponent key={`flat-table-${tableData.queryKey ?? i}`} tableData={tableData} query={query}>
                {' '}
              </TableComponent>
            ))}
          </Grid>
        )}
        <div className={classes.printSection}>
          <div className={classes.exportActions}>
            <Button
              variant={'contained'}
              onClick={printComponent}
              className={classes.printButton}
              disabled={isCapturingSnapshot}
            >
              <Typography>
                <Print />
                {isCapturingSnapshot ? 'Preparing Snapshot...' : 'Print'}
              </Typography>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

AnalyticsTable.propTypes = {
  chartDataArray: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      addOn: PropTypes.string,
      queryKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  tableDataArray: PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      queryKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  sectionDisplayOrder: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
    })
  ),
};

export default AnalyticsTable;
